import {
  DEFAULT_DATE_FORMAT,
  FORMAT_DATE,
  FORMAT_DATE_LLLL,
  FORMAT_DATE_TIME,
  FORMAT_DATE_TIME_ZONE,
  FORMAT_DATE_UTC,
  FORMAT_TIME,
} from '@/constants/date-formats';
import { tz, TZDate } from '@date-fns/tz';
import { utc } from '@date-fns/utc';
import { format, formatISO, getMonth, getYear, isValid, parse } from 'date-fns';

export const isMonthAfterToday = (selectedYear: number, month: number, timezone: string) => {
  const today = getCurrentDateInTimezone(timezone);
  const currentYear = getYear(today);
  const currentMonth = getMonth(today) + 1;

  if (selectedYear > currentYear || selectedYear < currentYear) {
    return false;
  }

  return month > currentMonth;
};

export const getCurrentDateInTimezone = (timezone: string) => {
  return tz(timezone)(new Date()).toString();
};

export const convertDateToTimezone = (date: string, timezone: string) => {
  return tz(timezone)(new Date(date));
};

export const formatToIsoUtcString = (date: Date | string) => formatISO(utc(date));

export const formatDateToTimezoneString = (date: Date | string, timezone: string) => {
  const dateInTimezone = tz(timezone)(date);
  return format(dateInTimezone, FORMAT_DATE_TIME);
};

export const formatDateToUtcString = (date: string) => {
  if (date.endsWith('Z')) return date;
  return format(date, FORMAT_DATE_UTC);
};

export const formatDateToDateString = (date: string, timezone: string) => {
  const formattedDate = formatDateToUtcString(date);
  return format(tz(timezone)(formattedDate), FORMAT_DATE);
};

export const formatDateToTimeString = (date: string, timezone: string) => {
  const formattedDate = formatDateToUtcString(date);
  return format(tz(timezone)(formattedDate), FORMAT_TIME);
};

export const formatDateToDateTimeString = (date: string, timezone: string) => {
  const formattedDate = formatDateToUtcString(date);
  return format(tz(timezone)(formattedDate), FORMAT_DATE_TIME);
};

export const formatDateToTimeZoneString = (date: string, timezone: string) => {
  const formattedDate = formatDateToUtcString(date);
  return format(tz(timezone)(formattedDate), FORMAT_DATE_TIME_ZONE);
};

export const formatDateToFriendlyString = (date: string, timezone: string) => {
  const formattedDate = formatDateToUtcString(date);
  return format(tz(timezone)(formattedDate), FORMAT_DATE_LLLL);
};

export const parseInputDate = (dateString: string, timezone: string) => {
  const parsedDate = parseFormattedDate(dateString, timezone);

  if (isValid(parsedDate)) {
    return parsedDate;
  }

  return null;
};

export const parseFormattedDate = (dateString: string, timezone: string) => {
  return parse(dateString, DEFAULT_DATE_FORMAT, new TZDate(), { in: tz(timezone) });
};

export const sanitizeDateInputString = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');

  if (cleanedValue.length <= 2) {
    return cleanedValue;
  } else if (cleanedValue.length <= 4) {
    return `${cleanedValue.slice(0, 2)}/${cleanedValue.slice(2)}`;
  } else {
    return `${cleanedValue.slice(0, 2)}/${cleanedValue.slice(2, 4)}/${cleanedValue.slice(4, 8)}`;
  }
};
